import React from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import RouteIcon from "@mui/icons-material/Route";
import { PublishedWorkShiftDto } from "../../models/publishedWorkShift/publishedWorkShift";
import { Base } from "../../framework/base";
import { useTranslation } from "react-i18next";

interface PlannedShiftCardProps {
    shifts: PublishedWorkShiftDto[];
    date: string;
}

const formatPlannedShiftTime = (
    startTime: string,
    endTime: string,
    date: string
): string => {
    const sameDay = Base.dayjsToJsonDate(startTime) === date;
    if (sameDay) {
        return `${Base.dayjsToTimeStr(startTime)} - ${Base.dayjsToTimeStr(endTime)}`;
    }
    return `${Base.dayjsToDateStrShort(startTime)} - ${Base.dayjsToDateStrShort(endTime)}`;
};

export const WorkTimeDetailsPlannedShiftCard = ({
    shifts,
    date,
}: PlannedShiftCardProps) => {
    const { t } = useTranslation();

    if (!shifts || shifts.length === 0) return null;

    const multipleShifts = shifts.length > 1;

    return (
        <Card
            variant="outlined"
            sx={{
                backgroundColor: "#f8f9fa",
                width: "fit-content",
            }}
        >
            <Typography
                variant="caption"
                fontWeight={600}
                sx={{ px: 2, pt: 1, display: "block" }}
            >
                {multipleShifts
                    ? t("plannedWorkShifts")
                    : t("plannedWorkShift")}
            </Typography>
            <CardContent
                sx={{
                    py: 1.5,
                    px: 0,
                    "&:last-child": { pb: 1.5 },
                    display: "flex",
                    flexDirection: "row",
                }}
            >
                {shifts.map((shift, index) => (
                    <React.Fragment key={shift.id}>
                        {index > 0 && (
                            <Divider orientation="vertical" flexItem />
                        )}
                        <Box sx={{ px: 2 }}>
                            <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                                mb={0.5}
                            >
                                <AccessTimeIcon fontSize="small" />
                                <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    sx={{ whiteSpace: "nowrap" }}
                                >
                                    {formatPlannedShiftTime(
                                        shift.startTime,
                                        shift.endTime,
                                        date
                                    )}
                                </Typography>
                                {shift.label && (
                                    <Chip
                                        label={shift.label}
                                        size="small"
                                        sx={{ height: 20, fontSize: "0.75rem" }}
                                    />
                                )}
                            </Box>
                            <Box
                                display="flex"
                                flexWrap="wrap"
                                alignItems="center"
                                gap={0.5}
                            >
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={0.5}
                                >
                                    <LocalShippingIcon fontSize="small" />
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={
                                            !shift.vehicleName
                                                ? { fontStyle: "italic" }
                                                : undefined
                                        }
                                    >
                                        {shift.vehicleName || t("noVehicle")}
                                    </Typography>
                                </Box>
                                {shift.routeName && (
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={0.5}
                                    >
                                        <RouteIcon fontSize="small" />
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{
                                                display: "-webkit-box",
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                            }}
                                        >
                                            {shift.routeName}
                                            {shift.routeComment &&
                                                ` - ${shift.routeComment}`}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </React.Fragment>
                ))}
            </CardContent>
        </Card>
    );
};
